import React, { useEffect, useState } from "react";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import CancelIcon from "@mui/icons-material/Cancel";
import { useQuery, UseQueryResult } from "react-query";
import { GET_ALL_COMMENTS } from "./queryKey";
import {
  CommentResponse,
  GetAllCommentsResponse,
} from "../../Helpers/backendResponseInterfaces";
import { getAllComments } from "../../Helpers/backendHelper";
import { DEFAULT_COMMENT_PAGE_SIZE, USER_TYPES } from "../../Helpers/constants";
import { Avatar } from "@mui/material";
import WriteComment from "./writeComment";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { formatCreatedAt, getUserData } from "../../Helpers/utils";
import { JumpIN } from "../utils/buttons";

interface CommentsProps {
  postId: number;
  comment: string;
  setIsCommentsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setComment: React.Dispatch<React.SetStateAction<string>>;
  setCommentCount: React.Dispatch<React.SetStateAction<number>>;
  needMargin?: boolean;
}

interface CommentProps {
  postId: number;
  comments: number;
  isCommentsOpen: boolean;
  setIsCommentsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const Comment: React.FC<CommentResponse> = (comment) => {
  return (
    <div key={comment.id} className="w-full p-1 bg-white">
      <div className="flex flex-row items-center gap-2">
        <Avatar
          sx={{ width: 24, height: 24 }}
          alt={comment.user.name}
          src={comment.user.profile_pic_url}
        />
        <div className="flex flex-col items-start justify-center flex-grow">
          <div className="text-xs font-thin w-full flex justify-between items-center">
            <div className="text-base font-extralight">{comment.user.name}</div>
            {comment.user.user_type === USER_TYPES.professional && (
              <div className="m-1 p-1 text-white bg-yellow-500 rounded-md font-bold text-xs cursor-default">
                {comment.user.user_type}
              </div>
            )}
          </div>
          <div className="text-xs font-thin">
            {formatCreatedAt(
              comment.metadata.created_at,
              comment.metadata.since_time,
            )}
          </div>
        </div>
      </div>
      <div className="w-full hover:bg-slate-100 transition-all duration-300 rounded p-3 break-words">
        {comment.content}
      </div>
    </div>
  );
};

export const Comments: React.FC<CommentsProps> = ({
  postId,
  comment,
  setIsCommentsOpen,
  setComment,
  setCommentCount,
  needMargin = true,
}) => {
  const user = getUserData();
  const [page, setPage] = useState<number>(1);
  const [noMoreComments, setNoMoreComments] = useState<boolean>(false);
  const [noComments, setNoComments] = useState<boolean>(false);
  const [comments, setComments] = React.useState<CommentResponse[]>([]);
  const { isLoading } = useQuery({
    queryKey: [GET_ALL_COMMENTS, page],
    queryFn: () =>
      getAllComments(postId, { page, size: DEFAULT_COMMENT_PAGE_SIZE }),
    onSuccess: (data: { data: GetAllCommentsResponse }) => {
      const comments = data.data;
      setComments((prevComments) => [...prevComments, ...comments.items]);
      if (comments.total === 0) {
        setNoComments(true);
        setNoMoreComments(true);
      }
      if (comments.items.length === 0) {
        setNoMoreComments(true);
      }
    },
    enabled: user ? true : false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  }) as UseQueryResult<GetAllCommentsResponse, unknown>;

  useEffect(() => {
    if (comments.length > 0) {
      setNoComments(false);
    }
  }, [comments]);

  return (
    <div
      className={`flex flex-col justify-between gap-3 bg-slate-50 w-96 h-96 rounded-lg ${
        needMargin ? "-mr-40" : ""
      } items-center p-2 z-50`}
    >
      <div className="flex flex-row items-center justify-between w-full">
        <div className="font-light antialiased">Thoughts</div>
        <button
          className="relative text-white rounded-full hover:p-0.5 duration-300 hover:bg-slate-200"
          onClick={() => setIsCommentsOpen(false)}
        >
          <CancelIcon color="error" />
        </button>
      </div>
      {user ? (
        <>
          <div className="flex flex-col gap-2 w-full basis-full overflow-y-auto">
            {isLoading && <div>Loading comments...</div>}
            {noComments && <div>No comments found</div>}
            <div className="flex flex-col gap-2 overflow-y-auto">
              {comments.map((comment) => (
                <Comment key={comment.id} {...comment} />
              ))}
              {!isLoading && !noMoreComments && (
                <div
                  onClick={() => setPage((prevPage) => prevPage + 1)}
                  className="cursor-pointer opacity-50 w-full flex items-center justify-center gap-1 hover:opacity-75"
                >
                  <AddCircleIcon color="inherit" />
                  <span>{noComments ? "Reload" : "Load more"}</span>
                </div>
              )}
            </div>
          </div>
          <WriteComment
            comment={comment}
            setComment={setComment}
            setComments={setComments}
            postId={postId}
            setCommentCount={setCommentCount}
          />
        </>
      ) : (
        <div className="text-center basis-full">
          <span className="my-2">Wanna get involved?</span>
          <JumpIN />
        </div>
      )}
    </div>
  );
};

export const CommentButton: React.FC<CommentProps> = ({
  comments,
  isCommentsOpen,
  setIsCommentsOpen,
}) => {
  return (
    <div
      className="flex items-center gap-1 cursor-pointer transition-all duration-300"
      onClick={() => setIsCommentsOpen(!isCommentsOpen)}
    >
      <div>
        {isCommentsOpen ? <ChatBubbleIcon /> : <ChatBubbleOutlineIcon />}
      </div>
      {comments !== 0 && (
        <span>
          {comments}{" "}
          <span className="hidden md:inline">
            Thought{comments > 1 ? "s" : ""}
          </span>
        </span>
      )}
    </div>
  );
};
